import { ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Card } from "../../../components/Card";
import { ReadinessDial } from "../../../components/ReadinessDial";
import { StatusBadge } from "../../../components/StatusBadge";
import type { JobPost } from "../../../types";

function formatBudget(job: JobPost): string {
  if (job.budgetMin !== undefined && job.budgetMax !== undefined) {
    return `$${job.budgetMin} to $${job.budgetMax}`;
  }
  if (job.budgetMin !== undefined) {
    return `From $${job.budgetMin}`;
  }
  if (job.budgetMax !== undefined) {
    return `Up to $${job.budgetMax}`;
  }
  return "Not listed";
}

export function JobRow({ job }: { job: JobPost }) {
  const toneClass =
    job.readiness.level === "GREEN"
      ? "row-stripe-green"
      : job.readiness.level === "AMBER"
        ? "row-stripe-amber"
        : "row-stripe-red";

  return (
    <Card
      className={`group row-stripe overflow-hidden ${toneClass} hover:border-accent-200 hover:bg-surface-raised`}
      interactive
      padding="none"
      variant="surface"
    >
      <Link
        className="flex flex-col gap-4 p-4 md:flex-row md:items-center md:p-5"
        to={`/jobs/${job.id}`}
      >
        <div className="flex shrink-0 items-center gap-3 md:block md:w-16 md:text-center">
          <ReadinessDial
            label="Detail"
            level={job.readiness.level}
            score={job.readiness.score}
            size="sm"
          />
          <span className="text-tabular mt-1 block text-xs font-semibold text-text-subtle md:mt-2">
            {job.readiness.score}/100
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="cell-label flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-accent-700">{job.category.replace(/_/g, " ")}</span>
            <span aria-hidden="true">-</span>
            <StatusBadge
              level={job.readiness.level}
              label={`${job.readiness.level} ${job.readiness.score}`}
              size="sm"
              tone="glyph"
            />
          </div>
          <h2 className="mt-2 truncate font-sans text-xl font-semibold leading-tight text-text-strong group-hover:text-accent-700">
            {job.title}
          </h2>
          <p className="mt-2 line-clamp-2 text-sm leading-6 text-text-muted">{job.summary}</p>
          <p className="mt-3 flex flex-wrap gap-x-2 gap-y-1 text-[var(--text-meta)] font-semibold text-text-subtle">
            <span>{job.neighborhood}</span>
            <span aria-hidden="true">-</span>
            <span>{job.desiredWindow}</span>
            <span aria-hidden="true">-</span>
            <span>{job.urgency}</span>
            <span aria-hidden="true">-</span>
            <span className="tabular">{formatBudget(job)}</span>
          </p>
        </div>
        <ChevronRight
          aria-hidden="true"
          className="hidden h-5 w-5 shrink-0 text-text-subtle transition group-hover:translate-x-1 group-hover:text-accent-700 md:block"
          strokeWidth={1.5}
        />
      </Link>
    </Card>
  );
}
